import { useEffect, useRef, useState } from 'react';
import { useChat } from '@ai-sdk/react';
import {
  MessageSquare,
  X,
  Send,
  Loader2,
  Paperclip,
  Sparkles,
  Mic,
  Square,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { authedFetch } from '@/lib/authed-fetch';
import type {
  AssistantClientSelectionCandidate,
  AssistantCaseSelectionCandidate,
  AssistantDocumentTemplateCandidate,
} from '@/lib/api-client';
import { AssistantMessage } from './assistant-message';
import { AttachmentThumbnails } from './attachment-thumbnails';
import { useVoiceRecorder } from './use-voice-recorder';
import {
  ATTACHMENT_INPUT_ACCEPT,
  MAX_ATTACHMENTS_PER_MESSAGE,
  prepareAttachment,
  validateAttachmentSelection,
  type PendingAttachment,
} from '@/lib/assistant-attachments';
import { formatElapsed, isVoiceRecordingSupported } from '@/lib/assistant-voice';

const CHAT_ENDPOINT = '/api/assistant/chat';

/**
 * Mensagem de "assistente nao configurado". Quando o backend nao tem a chave
 * OpenAI, o endpoint responde 503 (ServiceUnavailableException) ANTES de abrir
 * o stream; o useChat poe o corpo/status no error.message.
 */
const NOT_CONFIGURED_MSG =
  'Assistente não configurado. Configure a chave OpenAI no painel admin.';

/**
 * Traduz o erro do useChat numa mensagem util ao usuario (item 6 do escopo).
 *
 * Hoje qualquer falha virava "Erro ao conectar". Aqui distinguimos:
 *  - 503 / chave ausente -> instrucao clara de configurar a chave.
 *  - demais erros -> status + mensagem do servidor, para ser DIAGNOSTICAVEL
 *    quando o usuario testar com a chave real (ex: 429 rate limit, 401 chave
 *    invalida na OpenAI, 500 etc.).
 *
 * O useChat lanca um Error cujo `.message` costuma ser o CORPO da resposta
 * non-2xx. Como o AllExceptionsFilter do api responde JSON `{ message,
 * statusCode }`, tentamos extrair isso; se nao for JSON, usamos o texto cru.
 */
function describeChatError(error: Error | undefined): string | null {
  if (!error) return null;
  const raw = error.message ?? '';
  const low = raw.toLowerCase();

  // Caso chave ausente: 503 ou texto caracteristico do backend.
  if (
    low.includes('503') ||
    low.includes('service unavailable') ||
    low.includes('indisponivel') ||
    low.includes('openai') ||
    low.includes('configform') ||
    low.includes('configure a chave')
  ) {
    return NOT_CONFIGURED_MSG;
  }

  // Tenta extrair { message, statusCode } de um corpo JSON embutido na string.
  const parsed = tryParseEmbeddedJson(raw);
  if (parsed) {
    const status = parsed.statusCode ? `Erro ${parsed.statusCode}` : 'Erro';
    if (parsed.statusCode === 503) return NOT_CONFIGURED_MSG;
    return parsed.message ? `${status}: ${parsed.message}` : status;
  }

  // Fallback: status numerico solto na mensagem.
  const statusMatch = /\b(4\d{2}|5\d{2})\b/.exec(raw);
  if (statusMatch) {
    if (statusMatch[1] === '503') return NOT_CONFIGURED_MSG;
    return raw.trim()
      ? `Erro ${statusMatch[1]}: ${raw.trim().slice(0, 200)}`
      : `Erro ${statusMatch[1]} ao falar com o assistente.`;
  }

  // Sem status reconhecido: mostra o texto do servidor se houver (diagnostico),
  // senao a mensagem generica de conexao.
  if (raw.trim() && low !== 'failed to fetch') {
    return `Erro ao falar com o assistente: ${raw.trim().slice(0, 200)}`;
  }
  return 'Erro ao conectar com o assistente. Tente novamente.';
}

/** Extrai o primeiro objeto JSON com `message`/`statusCode` de uma string. */
function tryParseEmbeddedJson(
  raw: string,
): { message?: string; statusCode?: number } | null {
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start === -1 || end <= start) return null;
  try {
    const obj = JSON.parse(raw.slice(start, end + 1)) as Record<string, unknown>;
    const message = typeof obj.message === 'string' ? obj.message : undefined;
    const statusCode =
      typeof obj.statusCode === 'number' ? obj.statusCode : undefined;
    if (message === undefined && statusCode === undefined) return null;
    return { message, statusCode };
  } catch {
    return null;
  }
}

export function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Anexos (rodadas 2 e 3 "anexos e voz", ADR-0015): estado local da mensagem
  // sendo composta. Cada arquivo passa por validateAttachmentSelection
  // (tipo/tamanho/quantidade/teto agregado) e depois por prepareAttachment —
  // que redimensiona em canvas se for imagem, ou le cru se for PDF/.docx —
  // antes de virar um PendingAttachment pronto para preview e envio.
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [processingCount, setProcessingCount] = useState(0);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    append,
    setInput,
    isLoading,
    error,
  } = useChat({
    api: CHAT_ENDPOINT,
    fetch: authedFetch,
    credentials: 'include',
    maxSteps: 5,
    onError: () => {
      // erro tratado via `error` do hook
    },
  });

  // Suporte a gravacao de voz (rodada 4 "anexos e voz"): calculado uma unica
  // vez — depende so de APIs do browser (getUserMedia/MediaRecorder), que nao
  // mudam durante a sessao. Sem isto o botao de microfone nao aparece.
  const [voiceSupported] = useState(isVoiceRecordingSupported);

  /**
   * Texto transcrito vira o INPUT para o usuario revisar/editar — NUNCA
   * envia a mensagem sozinho (item 7 do escopo). Concatena em vez de
   * sobrescrever quando ja ha texto digitado.
   */
  function handleTranscribed(text: string) {
    setInput((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  const voice = useVoiceRecorder(handleTranscribed);

  /**
   * Processa arquivos recem-selecionados no <input type="file">: valida
   * (tipo/tamanho/teto de 3 por mensagem/teto agregado de bytes) e prepara os
   * aceitos — imagem passa pelo resize em canvas, documento (PDF/.docx) vai
   * cru. Erros de validacao E de processamento viram uma mensagem clara e
   * curta em vez de falhar silenciosamente.
   */
  async function handleFilesSelected(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    // O teto agregado precisa considerar o que ja esta anexado, senao daria
    // para fura-lo anexando um arquivo grande de cada vez.
    const existingBytes = attachments.reduce((sum, a) => sum + a.byteSize, 0);
    const { accepted, errors } = validateAttachmentSelection(
      files,
      attachments.length,
      existingBytes,
    );
    setAttachmentError(errors.length > 0 ? errors.join(' ') : null);
    if (accepted.length === 0) return;

    setProcessingCount((n) => n + accepted.length);
    const results = await Promise.allSettled(accepted.map((file) => prepareAttachment(file)));
    setProcessingCount((n) => Math.max(0, n - accepted.length));

    const ready: PendingAttachment[] = [];
    let failures = 0;
    for (const result of results) {
      if (result.status === 'fulfilled') ready.push(result.value);
      else failures += 1;
    }
    if (ready.length > 0) setAttachments((prev) => [...prev, ...ready]);
    if (failures > 0) {
      const msg =
        failures === 1
          ? 'Não foi possível processar um dos arquivos.'
          : `Não foi possível processar ${failures} dos arquivos.`;
      setAttachmentError((prev) => (prev ? `${prev} ${msg}` : msg));
    }
  }

  function handleRemoveAttachment(id: string) {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }

  /**
   * Submit customizado: alem do texto, monta `experimental_attachments` a
   * partir dos anexos ja preparados (API do AI SDK 4 — ver
   * lib/assistant-attachments.ts para a confirmacao de versao) e limpa o
   * estado de anexos apos disparar. `allowEmptySubmit` e necessario porque o
   * useChat recusa silenciosamente o submit quando `input` esta vazio — sem
   * isso, mandar SO um arquivo (sem digitar nada) nao faria nada.
   */
  function handleFormSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isLoading || processingCount > 0) return;
    if (!input.trim() && attachments.length === 0) return;

    const experimentalAttachments = attachments.map((a) => ({
      name: a.name,
      contentType: a.contentType,
      url: a.dataUrl,
    }));

    void handleSubmit(event, {
      experimental_attachments:
        experimentalAttachments.length > 0 ? experimentalAttachments : undefined,
      allowEmptySubmit: experimentalAttachments.length > 0,
    });
    setAttachments([]);
    setAttachmentError(null);
  }

  /**
   * Clique num candidato do card de desambiguacao (select_client): reusa o
   * MESMO fluxo de envio de mensagem do chat (append do useChat, o mesmo
   * mecanismo por tras do handleSubmit) para o modelo retomar a acao
   * pendente com o clientId certo. O card ja se desabilita sozinho apos o
   * clique (ClientSelectionCard controla seu proprio estado local).
   */
  function handleSelectClient(candidate: AssistantClientSelectionCandidate) {
    const docPart = candidate.document
      ? `documento ${candidate.document}`
      : 'sem documento cadastrado';
    void append({
      role: 'user',
      content: `Selecionei o cliente ${candidate.name} (${docPart}, id ${candidate.id}).`,
    });
  }

  /**
   * Clique num candidato do card de escolha de PROCESSO filtrado por
   * cliente (select_case): mesmo mecanismo do handleSelectClient. O modelo
   * recebe o caseId e segue o fluxo (ex: inspecionar variaveis do documento
   * com o processo resolvido).
   */
  function handleSelectCase(candidate: AssistantCaseSelectionCandidate) {
    const label = candidate.numeroCnj ?? 'sem número CNJ';
    void append({
      role: 'user',
      content: `Selecionei o processo ${label} (id ${candidate.id}).`,
    });
  }

  /**
   * Clique num candidato do card de escolha de modelo
   * (select_document_template): mesmo mecanismo do handleSelectClient. O
   * modelo recebe o templateId e segue o fluxo (inspecionar variaveis ->
   * perguntar o que falta -> propor a geracao).
   */
  function handleSelectTemplate(candidate: AssistantDocumentTemplateCandidate) {
    void append({
      role: 'user',
      content: `Selecionei o modelo "${candidate.name}" (id ${candidate.id}).`,
    });
  }

  // Scroll automatico ao fim das mensagens
  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  // Foco no input ao abrir
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Fechar o painel durante uma gravacao cancela (nao transcreve) e libera o
  // microfone — sem isto o indicador de gravacao do browser ficaria ligado
  // com o painel escondido (o widget so muda opacidade ao fechar, nao
  // desmonta).
  useEffect(() => {
    if (!open && voice.status === 'recording') voice.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const errorMessage = describeChatError(error);
  const voiceBusy = voice.status !== 'idle';

  return (
    <>
      {/* Backdrop para fechar ao clicar fora */}
      {open && (
        <div
          className="fixed inset-0 z-40"
          aria-hidden="true"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Painel do chat */}
      <div
        className={cn(
          'fixed bottom-20 right-4 z-50 flex flex-col',
          'w-[calc(100vw-2rem)] max-w-sm',
          'rounded-xl border border-[var(--border)] bg-[var(--bg)] shadow-2xl',
          'transition-all duration-200 ease-out origin-bottom-right',
          open
            ? 'scale-100 opacity-100 pointer-events-auto'
            : 'scale-95 opacity-0 pointer-events-none',
        )}
        style={{ height: '520px' }}
        role="dialog"
        aria-label="Assistente de IA"
      >
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-[var(--border)] px-4 py-3 shrink-0">
          <Sparkles className="h-4 w-4 text-[var(--accent)]" />
          <span className="flex-1 text-sm font-semibold text-[var(--fg)]">
            Assistente
          </span>
          <button
            onClick={() => setOpen(false)}
            className="rounded p-0.5 text-[var(--fg-muted)] hover:text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] transition-colors"
            aria-label="Fechar assistente"
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Mensagens */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 min-h-0">
          {messages.length === 0 && !isLoading && (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
              <Sparkles className="h-8 w-8 text-[var(--accent)] opacity-60" />
              <p className="text-sm text-[var(--fg-muted)]">
                Pergunte sobre clientes, processos ou qualquer dado do escritório.
              </p>
            </div>
          )}

          {messages.map((msg) => (
            <AssistantMessage
              key={msg.id}
              message={msg}
              onSelectClient={handleSelectClient}
              onSelectCase={handleSelectCase}
              onSelectTemplate={handleSelectTemplate}
            />
          ))}

          {isLoading && messages.at(-1)?.role !== 'assistant' && (
            <div className="flex items-center gap-2.5">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_oklch,var(--fg)_10%,transparent)]">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--fg-muted)]" />
              </div>
              <div className="flex gap-1">
                <span className="h-2 w-2 rounded-full bg-[var(--fg-muted)] animate-bounce [animation-delay:0ms]" />
                <span className="h-2 w-2 rounded-full bg-[var(--fg-muted)] animate-bounce [animation-delay:150ms]" />
                <span className="h-2 w-2 rounded-full bg-[var(--fg-muted)] animate-bounce [animation-delay:300ms]" />
              </div>
            </div>
          )}

          {errorMessage && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
              {errorMessage}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Anexos: preview + input */}
        <div className="border-t border-[var(--border)] shrink-0">
          <AttachmentThumbnails
            attachments={attachments}
            processingCount={processingCount}
            onRemove={handleRemoveAttachment}
            disabled={isLoading}
          />

          {attachmentError && (
            <p className="px-3 pt-2 text-xs text-red-600 dark:text-red-400">
              {attachmentError}
            </p>
          )}

          {voice.error && (
            <p className="px-3 pt-2 text-xs text-red-600 dark:text-red-400">
              {voice.error}
            </p>
          )}

          {voice.status === 'recording' ? (
            // Indicador de gravacao: substitui o input normal enquanto grava
            // (tempo decorrido + parar/cancelar). Cancelar DESCARTA o audio.
            <div className="flex items-center gap-2 px-3 py-3">
              <div
                className={cn(
                  'flex flex-1 items-center gap-2 rounded-lg border px-3 py-2',
                  'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950',
                )}
              >
                <span
                  className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-red-500"
                  aria-hidden="true"
                />
                <span
                  className="flex-1 text-sm font-medium tabular-nums text-red-700 dark:text-red-400"
                  aria-live="polite"
                >
                  Gravando... {formatElapsed(voice.elapsedSeconds)}
                </span>
              </div>
              <button
                type="button"
                onClick={voice.cancel}
                aria-label="Cancelar gravação"
                title="Cancelar gravação (descarta o áudio)"
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                  'text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
                  'transition-colors',
                )}
              >
                <Trash2 className="h-4 w-4" />
              </button>
              <Button
                type="button"
                size="icon"
                onClick={voice.stop}
                aria-label="Parar gravação e transcrever"
                title="Parar e transcrever"
              >
                <Square className="h-3.5 w-3.5 fill-current" />
              </Button>
            </div>
          ) : (
            <form onSubmit={handleFormSubmit} className="flex items-center gap-2 px-3 py-3">
              <input
                ref={fileInputRef}
                type="file"
                accept={ATTACHMENT_INPUT_ACCEPT}
                multiple
                hidden
                onChange={(e) => {
                  void handleFilesSelected(e.target.files);
                  // Permite selecionar o MESMO arquivo de novo apos remove-lo.
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={
                  isLoading ||
                  voiceBusy ||
                  processingCount > 0 ||
                  attachments.length >= MAX_ATTACHMENTS_PER_MESSAGE
                }
                aria-label="Anexar arquivo"
                title="Anexar imagem, PDF ou Word"
                className={cn(
                  'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                  'text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
                  'transition-colors disabled:opacity-40 disabled:pointer-events-none',
                )}
              >
                <Paperclip className="h-4 w-4" />
              </button>
              <input
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                placeholder={
                  voice.status === 'transcribing' ? 'Transcrevendo áudio...' : 'Digite sua pergunta...'
                }
                disabled={isLoading || voice.status === 'transcribing'}
                className={cn(
                  'flex-1 rounded-lg border border-[var(--border)] bg-[var(--bg)]',
                  'px-3 py-2 text-sm text-[var(--fg)] placeholder:text-[var(--fg-muted)]',
                  'focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:ring-offset-0',
                  'disabled:opacity-50',
                )}
                autoComplete="off"
              />
              {voiceSupported && (
                <button
                  type="button"
                  onClick={voice.start}
                  disabled={isLoading || voice.status === 'transcribing'}
                  aria-label="Gravar áudio"
                  title="Gravar áudio"
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                    'text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
                    'transition-colors disabled:opacity-40 disabled:pointer-events-none',
                  )}
                >
                  {voice.status === 'transcribing'
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Mic className="h-4 w-4" />
                  }
                </button>
              )}
              <Button
                type="submit"
                size="icon"
                disabled={
                  isLoading ||
                  voiceBusy ||
                  processingCount > 0 ||
                  (!input.trim() && attachments.length === 0)
                }
                aria-label="Enviar"
              >
                {isLoading
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <Send className="h-4 w-4" />
                }
              </Button>
            </form>
          )}
        </div>
      </div>

      {/* FAB */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Fechar assistente' : 'Abrir assistente'}
        type="button"
        className={cn(
          'fixed bottom-4 right-4 z-50',
          'flex h-12 w-12 items-center justify-center rounded-full shadow-lg',
          'bg-[var(--accent)] text-[var(--accent-fg)]',
          'transition-all duration-200 hover:opacity-90 active:scale-95',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2',
        )}
      >
        {open
          ? <X className="h-5 w-5" />
          : <MessageSquare className="h-5 w-5" />
        }
      </button>
    </>
  );
}
